import imagemIniciativa from 'assets/img/2026/iniciativa_video.png';
import { getCategoriaCor, getCategoriaLabel, normalizeCategoryPremio } from 'utils/iniciativa-categoria';
import { convertSharePointToEmbed } from 'utils/fns';
import { api } from './api';
import { dedupedFetch } from './cache';

export interface ElogioEtapa1 {
  elogio: string;
  quantidade: number;
}

export interface ParticipanteEtapa1 {
  nome: string;
  matricula: string;
  confirmado: boolean;
  isOwner?: boolean;
}

export interface IniciativaEtapa1 {
  id: string;
  titulo: string;
  tema: string;
  categoriaTag: string;
  categoryPremio: string;
  corCategoria: string;
  descricao: string;
  resumo: string;
  resumoTexto: string;
  cadastradoPor: string;
  cadastradoData: string;
  inscritoPor: string;
  inscritoData: string;
  elogiosPorTipo: ElogioEtapa1[];
  totalFavoritos: number;
  totalElogios: number;
  participantes: ParticipanteEtapa1[];
  imagem: string;
  videoUrl?: string;
  videoUrlOriginal?: string;
  documentacao?: string;
  isMad: boolean;
  isRede: boolean;
}

const normalizePrefixo = (prefixo?: string | number | null) => {
  if (prefixo === null || prefixo === undefined) {
    return '';
  }

  return String(prefixo).trim();
};

const normalizeMatricula = (value?: string) => {
  const text = String(value || '').trim().toUpperCase();
  if (!text) {
    return '';
  }

  return text.startsWith('F') ? text : `F${text}`;
};

const formatDate = (value?: number | string) => {
  if (!value) return '';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const mapElogios = (log: any[]): { elogiosPorTipo: ElogioEtapa1[]; totalElogios: number } => {
  const elogios = log.filter((item) => item?.actionId === 'elogiar');
  const elogiosPorUsuario = new Map<string, { time: number; elogio?: string }>();

  elogios.forEach((item) => {
    const userId = String(item?.userId || '').trim();
    if (!userId) {
      return;
    }

    const time = Number(item?.time) || 0;
    const elogio = item?.attributes?.elogio;
    const current = elogiosPorUsuario.get(userId);

    if (!current || time >= current.time) {
      elogiosPorUsuario.set(userId, { time, elogio });
    }
  });

  const grouped = new Map<string, number>();
  Array.from(elogiosPorUsuario.values()).forEach((item) => {
    if (!item.elogio) return;
    grouped.set(item.elogio, (grouped.get(item.elogio) || 0) + 1);
  });

  const elogiosPorTipo = Array.from(grouped.entries()).map(([elogio, quantidade]) => ({
    elogio,
    quantidade,
  }));

  return {
    elogiosPorTipo,
    totalElogios: Array.from(elogiosPorUsuario.values()).filter((item) => Boolean(item.elogio)).length,
  };
};

const mapFavoritos = (log: any[]) => {
  const favoritos = log.filter((item) => item?.actionId === 'favoritar');
  const favoritosPorUsuario = new Map<string, { time: number; ativo: boolean }>();

  favoritos.forEach((item) => {
    const userId = String(item?.userId || '').trim();
    if (!userId) {
      return;
    }

    const time = Number(item?.time) || 0;
    const ativo = item?.attributes?.ativo !== false;
    const current = favoritosPorUsuario.get(userId);

    if (!current || time >= current.time) {
      favoritosPorUsuario.set(userId, { time, ativo });
    }
  });

  return Array.from(favoritosPorUsuario.values()).filter((item) => item.ativo).length;
};

const mapParticipantes = (item: any): ParticipanteEtapa1[] => {
  const confirmedPlayers = new Set<string>((item?.confirmedPlayers || []).map((value: string) => normalizeMatricula(value)));
  const ownerMatricula = normalizeMatricula(item?.owner);

  const participantes: ParticipanteEtapa1[] = (item?.team || []).map((member: any) => {
    const matricula = normalizeMatricula(member?.chave || member?._id || member?.id);
    const nome = String(member?.name || 'Participante').trim();

    return {
      nome,
      matricula,
      confirmado: confirmedPlayers.has(matricula),
      isOwner: ownerMatricula !== '' && ownerMatricula === matricula,
    };
  });

  const ownerName = String(item?.owner_name || 'Responsável').trim();
  if (ownerMatricula && !participantes.some((member) => member.matricula === ownerMatricula)) {
    participantes.unshift({
      nome: ownerName,
      matricula: ownerMatricula,
      confirmado: confirmedPlayers.has(ownerMatricula),
      isOwner: true,
    });
  }

  return participantes;
};

const mapIniciativa = (item: any): IniciativaEtapa1 => {
  const categoryPremio = normalizeCategoryPremio(item?.categoryPremio || item?.CategoryPremio || '');
  const categoriaTag = getCategoriaLabel(categoryPremio) || 'Joga Junto + ECBB';
  const corCategoria = getCategoriaCor(categoryPremio);

  const documento: Array<{ file?: string; type?: string }> = Array.isArray(item?.documento)
    ? item.documento
    : [];

  const videoDoc = documento.find((doc) => doc?.type === 'video' && doc?.file);
  const fileDoc = documento.find((doc) => doc?.type === 'file' && doc?.file);
  const videoUrlRaw = videoDoc?.file;
  const imagem = imagemIniciativa;
  const videoUrl = videoUrlRaw ? convertSharePointToEmbed(videoUrlRaw) || videoUrlRaw : undefined;

  const log: any[] = Array.isArray(item?.log) ? item.log : [];
  const { elogiosPorTipo, totalElogios } = mapElogios(log);
  const totalFavoritos = mapFavoritos(log);

  const ownerMatricula = normalizeMatricula(item?.owner);
  const ownerName = String(item?.owner_name || 'Responsável').trim();
  const tema = String(item?.indicator?.bloco || item?.indicator?.nome || 'Tema não informado');
  const resumoPergunta = item?.indicator?.nome
    ? `Como essa iniciativa contribuiu para ${item.indicator.nome}?`
    : 'Por que essa iniciativa foi fundamental para o atingimento do direcionador estratégico?';

  return {
    id: String(item?._id || ''),
    titulo: String(item?.title || 'Iniciativa'),
    tema,
    categoriaTag,
    categoryPremio,
    corCategoria,
    descricao: String(item?.description || ''),
    resumo: resumoPergunta,
    resumoTexto: String(item?.result || item?.description || ''),
    cadastradoPor: `${ownerName} (${ownerMatricula || '-'})`,
    cadastradoData: formatDate(item?.created),
    inscritoPor: String(item?.inscricao_player || '--'),
    inscritoData: formatDate(item?.dt_inscricao),
    elogiosPorTipo,
    totalFavoritos,
    totalElogios,
    participantes: mapParticipantes(item),
    imagem,
    videoUrl,
    videoUrlOriginal: videoUrlRaw,
    documentacao: fileDoc?.file,
    isMad: item?.mad === true,
    isRede: item?.rede !== undefined && item?.rede !== null,
  };
};

export const hasEtapa1Eligibility = (playerFields?: any): boolean => {
  const etapa1 = playerFields?.etapa1;
  if (!Array.isArray(etapa1)) return false;
  return etapa1.some((v: any) => String(v).toUpperCase() === 'ELBB');
};

export const getPrefixoFromPlayerFields = (playerFields?: any): string => {
  return normalizePrefixo(playerFields?.prefixo_etapa1);
};

export interface Etapa1Identifier {
  comite?: string;
  prefixo?: string;
}

export const getIdentifierFromPlayerFields = (playerFields?: any): Etapa1Identifier => {
  const prefixo = normalizePrefixo(playerFields?.prefixo_etapa1);
  return prefixo ? { prefixo } : {};
};

export interface ComiteMembro {
  _id: string;
  chave?: string;
  nome: string;
  nome_guerra?: string;
  prefixo?: number | string;
  funcao?: string;
  email?: string;
  diretoria?: string;
}

export const fetchComiteMembrosByCategoriaPrefixo = async (
  categoria: string,
  prefixo?: string | number | null
): Promise<ComiteMembro[]> => {
  const categoriaTrim = String(categoria || '').trim();
  const prefixoTrim = prefixo === null || prefixo === undefined ? '' : String(prefixo).trim();
  if (!categoriaTrim || !prefixoTrim) {
    return [];
  }

  const cacheKey = `comite-membros-${categoriaTrim}-${prefixoTrim}`;

  return dedupedFetch(cacheKey, async () => {
    // Coerção prefixo str/int e o $or de comitê vazio ficam no script do aggregate.
    const { data } = await api.post('/find/comite_membros_etapa1', {
      categoria: categoriaTrim,
      prefixo: prefixoTrim,
    });
    return Array.isArray(data) ? (data as ComiteMembro[]) : [];
  }, 60_000);
};

export const buscarValidacaoEtapa1PorPrefixo = async (
  identifier: Etapa1Identifier
): Promise<IniciativaEtapa1[]> => {
  const prefixo = identifier?.prefixo ? normalizePrefixo(identifier.prefixo) : '';
  if (!prefixo) return [];

  const cacheKey = `validacao-etapa1-elbb-${prefixo}`;

  return dedupedFetch(cacheKey, async () => {
    const prefixoNum = Number(prefixo);
    const prefixoParam = Number.isFinite(prefixoNum) ? prefixoNum : prefixo;
    const { data } = await api.post('/find/validacao_etapa1_elbb', { prefixo: prefixoParam });
    if (!Array.isArray(data)) return [];
    return data.map(mapIniciativa);
  }, 30_000);
};
